import { all, call, put, takeLatest, select } from 'redux-saga/effects';
import { saveAs } from 'file-saver';
import {
  actions as libraryActions,
  FETCH_PROJECT_LIBRARY,
  FETCH_BUILDING_NOTES_SPREADSHEET,
  fetchBuildingNotesSpreadsheetSuccess,
  fetchBuildingNotesSpreadsheetFailed,
} from 'spw-redux/modules/library/actions';
import { APIResponse } from 'spw-redux/types';
import { formatNameDownload } from 'spw-shared/utils';
import { handleError } from 'spw-redux/modules/UI/messages/actions';
import { IBuilding, IFloor } from 'spw-redux/modules/projects/types';
import { IPhoto } from 'spw-redux/modules/photo/types';
import moment from 'moment';
import { ISpace } from 'spw-redux/modules/floorplan/types';
import { SPACE_TYPE_CATEGORIES, XLSX_MIME_TYPE } from 'spw-constants';
import { snakeCase } from 'lodash';
import {
  getProjectLibrary as getProjectLibraryApi,
  fetchBuildingNotesSpreadsheet as fetchBuildingNotesSpreadsheetApi,
} from '../../../api/library';
import { IAsset, ILibrary } from './types';
import { selectClientFacingNamesAreVisible } from '../space_types/selectors';

function orderByCreationDate(list: IAsset[]) {
  return list.sort((a: IAsset, b: IAsset) => {
    const dateA = new Date(a.insertedAt);
    const dateB = new Date(b.insertedAt);
    return dateA.getTime() - dateB.getTime();
  });
}

function flattenAssetsList(library: ILibrary, category: keyof ISpace) {
  return library.buildings.map((building: IBuilding) => {
    const buildingAssetsList = building.floors.reduce(
      (assetsInBuilding: IAsset[], floor: IFloor) => {
        let assetsList: IAsset[] = [];
        const nameCounts: Record<string, number> = {};

        SPACE_TYPE_CATEGORIES.forEach((spaceTypeCategory: string) => {
          const categorySpaces = floor.spaces.filter(
            (floorSpaceType: ISpace) => {
              return (
                floorSpaceType.spaceType.spaceTypeCategory.category ===
                snakeCase(spaceTypeCategory)
              );
            }
          );

          const partialList = categorySpaces.reduce(
            (assetsInFloor: IAsset[], space: ISpace) => {
              if (category === 'photos') {
                const photos = space.photos.slice();

                photos.sort((photoA: IPhoto, photoB: IPhoto) => {
                  return moment(photoA.insertedAt, 'YYYY-MM-DD HH:mm:ss').diff(
                    moment(photoB.insertedAt, 'YYYY-MM-DD HH:mm:ss')
                  );
                });

                photos.forEach((asset: IPhoto, index: number) => {
                  const photo = asset;
                  let downloadName = formatNameDownload(
                    floor,
                    space,
                    index,
                    photo.format
                  );
                  const nameCount = nameCounts[downloadName];

                  if (!nameCount) {
                    nameCounts[downloadName] = 1;
                  } else {
                    nameCounts[downloadName] += 1;
                    downloadName = downloadName.replace(
                      `.${photo.format}`,
                      ` (${nameCount}).${photo.format}`
                    );
                  }
                  photo.downloadName = downloadName;
                  photo.index = index + 1;
                });
                return assetsInFloor.concat(photos);
              }

              return assetsInFloor.concat(space[category] as IAsset[]);
            },
            []
          );

          assetsList = assetsList.concat(partialList);
        });

        return assetsInBuilding.concat(assetsList);
      },
      []
    );

    return category === 'photos'
      ? buildingAssetsList
      : orderByCreationDate(buildingAssetsList);
  });
}

function* fetchProjectLibrary(
  action: ReturnType<typeof libraryActions.fetchProjectLibrary>
) {
  try {
    yield put(libraryActions.setLoadingLibrary(true));

    const { projectId, buildingId, floorId } = action.payload;
    const response: APIResponse<typeof getProjectLibraryApi> = yield call(
      getProjectLibraryApi,
      projectId
    );
    const { library } = response.data;
    const photosFlattened = flattenAssetsList(library, 'photos');
    const notesFlattened = flattenAssetsList(library, 'notes');

    library.buildings = library?.buildings?.map(
      (building: IBuilding, index: number) => ({
        ...building,
        photosListByDate: photosFlattened[index],
        notesListByDate: notesFlattened[index],
      })
    );

    yield put(libraryActions.fetchProjectLibrarySuccess(library));
    yield put(libraryActions.selectBuilding(buildingId, floorId));

    yield put(libraryActions.setLoadingLibrary(false));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* fetchBuildingNotesSpreadsheet(
  action: ReturnType<typeof libraryActions.fetchBuildingNotesSpreadsheet>
) {
  try {
    let timezone = 'America/Los_Angeles';
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { buildingId } = action.payload;
    const clientFacingNamesAreVisible: boolean = yield select(
      selectClientFacingNamesAreVisible
    );
    const {
      data,
    }: APIResponse<typeof fetchBuildingNotesSpreadsheetApi> = yield call(
      fetchBuildingNotesSpreadsheetApi,
      buildingId,
      timezone,
      clientFacingNamesAreVisible
    );

    saveAs(
      `data:${XLSX_MIME_TYPE};base64,${data.spreadsheet.data}`,
      data.spreadsheet.name
    );

    yield put(fetchBuildingNotesSpreadsheetSuccess());
  } catch (e) {
    yield put(fetchBuildingNotesSpreadsheetFailed());
    yield put(handleError(e));
  }
}

function* saga() {
  yield all([
    takeLatest(FETCH_PROJECT_LIBRARY, fetchProjectLibrary),
    takeLatest(FETCH_BUILDING_NOTES_SPREADSHEET, fetchBuildingNotesSpreadsheet),
  ]);
}

export default saga;
